// Card / modal chrome for FeatureChartCard.
//
// Renders the title + button row (cycler + expand) plus the body slot.
// In `card` variant it wraps in a white shadow card (used on the feature
// detail page). In `inline` variant it skips the outer card — the parent
// already provides a bordered tile (used in the project features list).
//
// Owns the expand state and the fullscreen modal. The body is invoked
// twice — once compact, once fullscreen — so it can pick a bigger
// layout / extra interactions when the modal is open.

import React, { useEffect, useState } from 'react';

interface Props {
  variant: 'card' | 'inline';
  title: React.ReactNode;
  // Render prop so controls can vary by surface (fullscreen vs inline).
  // CyclerButton uses this to expose fullscreen-only kebab items.
  controls?: (fullscreen: boolean) => React.ReactNode;
  // Body render prop. We pass `fullscreen` so the same body component
  // can swap layouts between the inline card and the modal.
  renderBody: (fullscreen: boolean) => React.ReactNode;
  // Title of the modal (defaults to `title`). Useful when the inline
  // title is interactive (a link) and the modal title should be plain.
  modalTitle?: React.ReactNode;
}

const ChartCardChrome: React.FC<Props> = ({ variant, title, controls, renderBody, modalTitle }) => {
  const [expanded, setExpanded] = useState(false);

  // Lock page scroll while the modal is open so the wheel doesn't scroll
  // the page hiding underneath when the user wheel-zooms a chart.
  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [expanded]);

  const expandButton = (
    <button
      onClick={() => setExpanded(true)}
      className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
      title="Expand chart"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
      </svg>
    </button>
  );

  const closeButton = (
    <button
      onClick={() => setExpanded(false)}
      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
      title="Close fullscreen"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );

  const headerRow = (
    <div className="flex justify-between items-start mb-3">
      <div className="pt-0.5">{title}</div>
      <div className="flex items-center gap-1">
        {controls?.(false)}
        {expandButton}
      </div>
    </div>
  );

  const inner = variant === 'card' ? (
    <div className="bg-white p-2 sm:p-6 rounded-lg shadow-md mb-8">
      {headerRow}
      {renderBody(false)}
    </div>
  ) : (
    <>
      {headerRow}
      {renderBody(false)}
    </>
  );

  return (
    <>
      {inner}
      {expanded && (
        <div className="fixed inset-x-0 top-0 h-dvh bg-white z-50 flex flex-col overflow-hidden">
          <div className="flex justify-between items-center p-4 text-gray-800">
            <div>{modalTitle ?? title}</div>
            <div className="flex items-center gap-1">
              {controls?.(true)}
              {closeButton}
            </div>
          </div>
          <div className="flex-1 min-h-0 bg-white px-4 pb-4 flex flex-col">
            <div className="flex-1 min-h-0">{renderBody(true)}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChartCardChrome;
